import { memo } from "react";
import { NavLink } from "@/shared/components/layout/NavLink";
import {
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarHeader, 
  SidebarFooter 
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useNavVisibility } from "@/hooks/useNavVisibility";
import { AccountContextCenter } from "@/components/layout/AccountContextCenter";
import { SidebarSkeleton } from "@/shared/components/layout/SidebarSkeleton";
import { Building } from "lucide-react";
import {
  platformItems,
  navigationItems,
  managementItems,
  observabilityItems,
  NavItem
} from "@/config/navigation";

/**
 * Renders a disabled nav item with "Soon" tooltip
 */
function ComingSoonNavItem({ item }: { item: NavItem }) {
  return (
    <SidebarMenuButton
      tooltip="Coming Soon"
      className="opacity-50 grayscale cursor-not-allowed select-none"
    >
      <item.icon className="h-4 w-4" />
      <span>{item.title}</span>
    </SidebarMenuButton>
  );
}

/**
 * Renders an enabled nav item with active state
 */
function EnabledNavItem({ item }: { item: NavItem }) {
  return (
    <SidebarMenuButton asChild>
      <NavLink
        to={item.url}
        end={item.end ?? false}
        className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        activeClassName="bg-sidebar-primary text-sidebar-primary-foreground font-medium"
      >
        <item.icon className="h-4 w-4" />
        <span>{item.title}</span>
      </NavLink>
    </SidebarMenuButton>
  );
}

/**
 * Renders a section of nav items (Platform, Navigation, Management)
 */
function NavSection({ 
  label, 
  items 
}: { 
  label: string; 
  items: NavItem[] 
}) {
  if (items.length === 0) return null;
  
  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-sidebar-foreground/70">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map(item => (
            <SidebarMenuItem key={item.title}>
              {item.comingSoon ? (
                <ComingSoonNavItem item={item} />
              ) : (
                <EnabledNavItem item={item} />
              )}
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export const AppSidebar = memo(function AppSidebar() {
  const { orgName, isOrgAdmin, isSuperAdmin, loading } = useAuth();
  const { filterNavItems, canSeePlatformSection } = useNavVisibility();

  // Show skeleton while auth context is loading to prevent flicker
  if (loading) {
    return <SidebarSkeleton />;
  }

  // Firm name display (using "Firm" terminology, DB stays as organization)
  const displayFirmName = orgName ?? 'LawAssist';

  // Filter nav items based on user permissions
  // Super Admins should ONLY see Platform section, not Firm/Navigation/Management
  const visiblePlatformItems = canSeePlatformSection ? platformItems : [];
  const visibleNavigationItems = isSuperAdmin ? [] : filterNavItems(navigationItems);
  const visibleManagementItems = isSuperAdmin ? [] : filterNavItems(managementItems);
  // Observability is visible to all staff roles (read-only for non-admin)
  const visibleObservabilityItems = isSuperAdmin ? [] : filterNavItems(observabilityItems);

  return (
    <Sidebar className="border-r border-sidebar-border" collapsible="icon">
      {/* Header - Firm branding only */}
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        {/* Show text only when expanded */}
        <div className="group-data-[collapsible=icon]:hidden">
          <h1 className="text-lg font-bold text-sidebar-foreground truncate" title={displayFirmName}>
            {displayFirmName}
          </h1>
          <p className="text-xs text-sidebar-foreground/70">Law Assistant</p>
        </div>
        
        {/* Show icon when collapsed */}
        <div className="hidden group-data-[collapsible=icon]:flex items-center justify-center">
          <Building className="h-6 w-6 text-sidebar-foreground" />
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Platform Section - Super Admin only */}
        {canSeePlatformSection && (
          <NavSection label="Platform" items={visiblePlatformItems} />
        )}

        {/* Main Navigation */}
        <NavSection label="Navigation" items={visibleNavigationItems} />

        {/* Management Section */}
        <NavSection label="Management" items={visibleManagementItems} />
        
        {/* Observability Section - All staff (read-only for non-admin roles) */}
        <NavSection label="Monitoring" items={visibleObservabilityItems} />
      </SidebarContent>
      
      {/* Footer - Account & Context Center */}
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <AccountContextCenter />
      </SidebarFooter>
    </Sidebar>
  );
});
